import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  Film, X, Loader2, Lock, Unlock, Save, RefreshCw, AlertCircle, Copy, Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShootingScriptModal — Stage 1 manual save / lock / regenerate flow for
 * the Opus 4.7-generated two-column shooting script.
 *
 * Mirrors the ScriptTemplatizerModal lifecycle (never_run → processing →
 * ready | failed) but talks to /shooting-script/* endpoints.
 *
 * Locks are mutually exclusive with the Templatized lock on the backend —
 * locking this auto-unlocks the templatized version (and vice versa).
 */
export default function ShootingScriptModal({
  open,
  onClose,
  projectId,
  authToken,
  onLocked,
}) {
  const [status, setStatus] = useState('never_run');
  const [locked, setLocked] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [originalMarkdown, setOriginalMarkdown] = useState('');
  const [error, setError] = useState('');
  const [scenesCount, setScenesCount] = useState(0);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchStatus = useCallback(async () => {
    if (!projectId || !authToken) return null;
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/shooting-script/status`,
        { headers },
      );
      const d = res.data;
      setStatus(d.status || 'never_run');
      setLocked(!!d.locked);
      setMarkdown(d.markdown || '');
      setOriginalMarkdown(d.markdown || '');
      setScenesCount(d.scenes_count || 0);
      setUsage(d.usage);
      setError(d.error || '');
      return d;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, authToken]);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open, fetchStatus]);

  // Poll while processing
  useEffect(() => {
    if (!open || status !== 'processing') return undefined;
    const id = setInterval(() => fetchStatus(), 3500);
    return () => clearInterval(id);
  }, [open, status, fetchStatus]);

  // Kicks off Opus 4.7 shooting-script generation. This endpoint clears
  // the content-hash cache server-side so calling it again forces a fresh
  // Opus call (used both for first-gen and re-run from the modal).
  const kickoff = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/generate-shooting-script`,
        {},
        { headers },
      );
      toast.success('AI is writing your shooting script…');
      setStatus('processing');
      fetchStatus();
    } catch (e) {
      toast.error(`Failed to start: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/projects/${projectId}/shooting-script`,
        { markdown },
        { headers },
      );
      toast.success('Edits saved');
      setOriginalMarkdown(markdown);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const lock = async () => {
    if (!window.confirm('Lock the Shooting Script as canonical? This auto-unlocks the Templatized lock if it was set. You can unlock anytime.')) return;
    if (markdown !== originalMarkdown) {
      const ok = window.confirm('You have unsaved edits. Save first?');
      if (ok) await save();
    }
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/shooting-script/lock`,
        {},
        { headers },
      );
      if (res.data?.auto_unlocked_templatized) {
        toast.success('Shooting Script locked · Templatized auto-unlocked');
      } else {
        toast.success('Shooting Script locked as canonical');
      }
      setLocked(true);
      if (onLocked) onLocked();
    } catch (e) {
      toast.error(`Lock failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const unlock = async () => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/shooting-script/unlock`,
        {},
        { headers },
      );
      toast.success('Unlocked — downstream stages now use the Original Upload again');
      setLocked(false);
      if (onLocked) onLocked();
    } catch (e) {
      toast.error(`Unlock failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const copyToClipboard = async () => {
    if (!markdown) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
      } else {
        const ta = document.createElement('textarea');
        ta.value = markdown;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Copy failed — your browser may have blocked it.');
    }
  };

  // Download the pre-generated two-column shooting script as .docx.
  // Uses the existing backend route at /shooting-script.docx which serves
  // a python-docx-rendered two-column (Visuals / Audio) Word file.
  const downloadAsDocx = async () => {
    if (!markdown) return;
    const tid = toast.loading('Preparing .docx…');
    try {
      const res = await fetch(
        `${API}/projects/${projectId}/shooting-script.docx`,
        { method: 'GET' },
      );
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); detail = j.detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `shooting-script-${(projectId || 'project').slice(0, 8)}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Shooting script downloaded', { id: tid });
    } catch (e) {
      toast.error(`Download failed: ${e.message || e}`, { id: tid });
    }
  };

  if (!open) return null;

  const dirty = markdown !== originalMarkdown;
  const isProcessing = status === 'processing';
  const isReady = status === 'ready';
  const isFailed = status === 'failed';

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="shooting-script-modal-backdrop"
    >
      <div
        className="w-full max-w-5xl h-[88vh] flex flex-col rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
        style={{ background: 'rgba(15, 22, 38, 0.98)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="shooting-script-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <Film className="w-4 h-4 text-purple-300 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">Shooting Script</div>
              <div className="text-[11px] text-white/50">
                AI · two-column visuals/audio · production-ready
                {locked && <span className="ml-2 text-amber-300">· LOCKED</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {usage && (
              <div className="hidden md:block text-[10px] text-white/40 font-mono">
                {usage.input_tokens}↓ {usage.output_tokens}↑ tokens
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="shooting-script-close-btn"
            >
              <X className="w-4 h-4 text-white/60" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {(status === 'never_run') && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <Film className="w-12 h-12 text-purple-300/40 mb-5" />
              <h3 className="text-lg font-semibold text-white mb-2">Generate Shooting Script</h3>
              <p className="text-sm text-white/60 max-w-md leading-relaxed mb-2">
                AI will write a detailed two-column shooting script (Visuals / Audio)
                ready for the crew. You can edit it and choose to lock it as the canonical
                source for all downstream stages instead of the Templatized form.
              </p>
              <p className="text-[12px] text-purple-200/80 max-w-md leading-relaxed mb-5">
                Locking this <strong>auto-unlocks the Templatized lock</strong> — only one
                source is canonical at a time.
              </p>
              <button
                onClick={kickoff}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/20 flex items-center gap-2 transition-all disabled:opacity-50"
                data-testid="shooting-script-kickoff-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
                Generate Shooting Script
              </button>
              <div className="mt-3 text-[11px] text-white/40">~60–120s · ~$0.30–0.80</div>
            </div>
          )}

          {isProcessing && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <Loader2 className="w-10 h-10 text-purple-300 animate-spin mb-4" />
              <div className="text-sm text-white/80 mb-1">AI is writing your shooting script…</div>
              <div className="text-[11px] text-white/40">This usually takes 60–120 seconds. The page will update automatically.</div>
            </div>
          )}

          {isFailed && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <AlertCircle className="w-10 h-10 text-rose-400 mb-4" />
              <div className="text-sm text-white/80 mb-1">Shooting script generation failed</div>
              <div className="text-[12px] text-rose-300/80 max-w-md mb-4">{error || 'Unknown error'}</div>
              <button
                onClick={kickoff}
                className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-purple-500/15 text-purple-200 hover:bg-purple-500/25 border border-purple-500/30 flex items-center gap-1.5 transition-all"
                data-testid="shooting-script-retry-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          )}

          {isReady && (
            <>
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-black/30 flex-shrink-0">
                <div className="text-[11px] text-white/60 font-mono">
                  {scenesCount} scene{scenesCount !== 1 ? 's' : ''}
                  {dirty && <span className="ml-3 text-amber-300">· unsaved edits</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={copyToClipboard}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                    title="Copy markdown to clipboard"
                    data-testid="shooting-script-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <button
                    onClick={downloadAsDocx}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                    title="Download two-column shooting script (.docx)"
                    data-testid="shooting-script-download-docx-btn"
                  >
                    <Download className="w-3.5 h-3.5 text-white/70" />
                  </button>
                  <div className="w-px h-5 bg-white/10 mx-0.5" />
                  {!locked && (
                    <>
                      <button
                        onClick={kickoff}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-white/5 text-white/80 hover:bg-white/10 border border-white/10 flex items-center gap-1.5 transition-all"
                        data-testid="shooting-script-rerun-btn"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Re-generate
                      </button>
                      <button
                        onClick={save}
                        disabled={saving || !dirty}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-purple-500/15 text-purple-200 hover:bg-purple-500/25 border border-purple-500/30 flex items-center gap-1.5 transition-all disabled:opacity-50"
                        data-testid="shooting-script-save-btn"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                      </button>
                      <button
                        onClick={lock}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30 flex items-center gap-1.5 transition-all"
                        data-testid="shooting-script-lock-btn"
                      >
                        <Lock className="w-3.5 h-3.5" /> Lock as canonical
                      </button>
                    </>
                  )}
                  {locked && (
                    <button
                      onClick={unlock}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 flex items-center gap-1.5 transition-all"
                      data-testid="shooting-script-unlock-btn"
                    >
                      <Unlock className="w-3.5 h-3.5" /> Unlock to edit
                    </button>
                  )}
                </div>
              </div>
              <textarea
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                readOnly={locked}
                spellCheck={false}
                className="flex-1 min-h-0 w-full px-5 py-4 bg-black/30 text-white/90 text-[13px] font-mono leading-relaxed border-none focus:outline-none resize-none"
                data-testid="shooting-script-markdown-editor"
                placeholder="Shooting script markdown will appear here…"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
